import { MUNICIPALITY_CODE_VIETNAM } from 'constants';

import React, { useCallback, useEffect } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { reportDetail } from 'api/endpoints/report';
import LayoutReview from 'components/layouts/LayoutReview';
import { ENUM_SURVEY_STATUS, ENUM_SURVEY_TYPE, ENUM_TYPE_ACTION, ENUM_TYPE_SURVEY, ENUM_TYPE_SURVEY_RESPONSE } from 'constants/enum';
import { MESSAGE_VIEW_SDQ, MESSAGE_VIEW_SDQ_VIETNAM } from 'constants/string/messages';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { handleError, handleImageSurvey } from 'utils/helper';

const ViewSDQ = ({ id, disableColor = false }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { me } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.common);

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      sections: [],
    };

    return initData;
  })();

  const form = useFormik({ initialValues: initialValues });

  const handleImage = async (images) => {
    return await handleImageSurvey(images);
  };

  const handleQuestion = async (question, survey_response) => {
    const processedImages = await handleImage(question.images);
    const answer = _.find(survey_response, { question_id: question.order })?.answer || null;

    return {
      ...question,
      action: ENUM_TYPE_ACTION.UPDATE,
      images: processedImages,
      answer: _.isArray(answer) ? answer : [answer],
      media_files: _.find(survey_response, { question_id: question.order })?.media_files || null,
    };
  };

  const handleSection = async (section, survey_response) => {
    const processedQuestions = [];
    let question_number = 0;
    for (const question of section.questions) {
      const questionHandled = await handleQuestion(question, survey_response);
      processedQuestions.push({
        ...questionHandled,
        question_number: question_number + 1,
        order: question_number,
      });
      question_number++;
    }

    return processedQuestions;
  };

  const handleOrder = (data, key) => {
    return data.map((item, index) => {
      return {
        ...item,
        [key]: index,
      };
    });
  };

  const handleSurveyContent = async (survey_content, survey_response, survey_types, content_types) => {
    let sortedSections = null;
    let responses = [];

    for (const [index, survey_type] of survey_types.entries()) {
      const content = survey_content.find((item) => item.survey_type === survey_type);
      if (!content) {
        break;
      }

      const sortedSection = _.sortBy(content.sections, 'order');
      if (sortedSections === null) {
        sortedSections = sortedSection;
      } else {
        sortedSections[0].questions = [...sortedSections[0].questions, ...sortedSection[0].questions];
      }

      const response = survey_response.find((item) => item.type === content_types[index]).responses;
      if (responses.length === 0) {
        responses = response;
      } else {
        responses = [...responses, ...response];
      }
    }

    if (!sortedSections) {
      return [];
    }

    sortedSections[0].questions = handleOrder(sortedSections[0].questions, 'order');
    responses = handleOrder(responses, 'question_id');

    const dataSection = [];
    for (const section of sortedSections) {
      const sectionHandled = await handleSection(section, responses);
      dataSection.push({
        ...section,
        action: ENUM_TYPE_ACTION.UPDATE,
        questions: sectionHandled,
      });
    }

    return dataSection;
  };

  /**
   * Processing the question map with the answer
   * Including 3 parts: SDQ, YABUNAKA, MCHAT_DIPOLE
   * Each part has the corresponding questions and answers
   */
  const handleSurvey = async (survey_content, survey_response) => {
    const sec_SDQ = await handleSurveyContent(survey_content, survey_response, [ENUM_TYPE_SURVEY.SDQ], [ENUM_TYPE_SURVEY_RESPONSE.SDQ]);
    const sec_YABUNAKA = await handleSurveyContent(
      survey_content,
      survey_response,
      [ENUM_TYPE_SURVEY.YABUNAKA],
      [ENUM_TYPE_SURVEY_RESPONSE.YABUNAKA]
    );
    const sec_MCHAT_DIPOLE = await handleSurveyContent(
      survey_content,
      survey_response,
      [ENUM_TYPE_SURVEY.MCHAT, ENUM_TYPE_SURVEY.DIPOLE],
      [ENUM_TYPE_SURVEY_RESPONSE.MCHAT, ENUM_TYPE_SURVEY_RESPONSE.DIPOLE]
    );
    const sections = [];

    const _MESSAGE_VIEW_SDQ = MUNICIPALITY_CODE_VIETNAM.includes(me?.municipality_code) ? MESSAGE_VIEW_SDQ_VIETNAM : MESSAGE_VIEW_SDQ;

    if (sec_SDQ.length > 0) {
      sections.push({ ...sec_SDQ[0], title: _MESSAGE_VIEW_SDQ.TITLE_SDQ, order: 0 });
    }

    if (sec_YABUNAKA.length > 0) {
      sections.push({ ...sec_YABUNAKA[0], title: _MESSAGE_VIEW_SDQ.TITLE_YABUNAKA, order: 1 });
    }

    if (sec_MCHAT_DIPOLE.length > 0) {
      sections.push({ ...sec_MCHAT_DIPOLE[0], title: _MESSAGE_VIEW_SDQ.TITLE_MCHAT_DIPOLE, order: 2 });
    }

    form.setValues({ sections: sections });
  };

  /**
   * Get detail report
   */
  const getDetail = async () => {
    try {
      const result = await reportDetail(id);
      if (result.data) {
        const survey_content = result.data.survey_content;
        const survey_response = result.data.report_content;
        await handleSurvey(survey_content, survey_response);
      } else {
        navigate(AppRoutes.report);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const getReportDetail = useCallback(async () => {
    if (me) {
      await getDetail();
    }
  }, [me, id]);

  useEffect(() => {
    getReportDetail();
  }, [getReportDetail]);

  useEffect(() => {
    document.body.classList.add('overflow-hidden-custom');
    document.body.classList.add('h-screen-custom');

    return () => {
      document.body.classList.remove('overflow-hidden-custom');
      document.body.classList.remove('h-screen-custom');
    };
  }, []);

  return (
    <>
      {form.values.sections.length > 0 && !loading.isOpen && (
        <section id="create-survey" className="flex-middle-start flex-wrap relative h-full">
          <div id="report-detail-container" className="flex justify-center items-start space-x-6 h-full">
            <div className="w-full h-full">
              <div className="pb-4 p-2" id="report-detail-title">
                <h1 className="text-lg font-bold text-black-102 text-center">{t('SDQ_TITLE')}</h1>
                <div className="text-center font-bold text-sm" dangerouslySetInnerHTML={{ __html: t('STRENGTH_DIFFICULTIES_QUESTIONNAIRE') }}></div>
                <div className="text-left text-sm mt-4" dangerouslySetInnerHTML={{ __html: t('SDQ_DESCRIPTION') }}></div>
                <p className="text-xs text-right mt-1">{t('SDQ_DESCRIPTION_AUTHOR')}</p>
              </div>
              <LayoutReview className="max-w-full h-full !px-2" id="layout-report-detail">
                <form id="report-detail-form" className="create-survey !h-[calc(100%-194px)] overflow-y-auto px-4">
                  <Form
                    isShowVietName={MUNICIPALITY_CODE_VIETNAM.includes(me?.municipality_code)}
                    form={form}
                    viewOnly={true}
                    showControlMenu={false}
                    showTitle={false}
                    disableColor={disableColor}
                  />
                </form>
              </LayoutReview>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default React.memo(ViewSDQ);
